import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { ImagePreviewModal } from '@/components/modals/ImagePreviewModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { logError } from '@/lib/errorLogging';

import {
  fetchScreenshotPreview,
  markScreenshotThumbUnavailable,
  refetchScreenshotThumb,
  useScreenshotThumbQuery,
} from './screenshotPreview.query';

type ScreenshotImageState =
  | { status: 'loading' }
  | { status: 'shown'; src: string }
  | { status: 'unavailable' }
  | { status: 'failed' };

// One object URL per mounted part, revoked when the part unmounts or the
// bytes change, so no image outlives the card that showed it
const useObjectUrl = (blob: Blob | undefined): string | undefined => {
  const [objectUrl, setObjectUrl] = useState<{ blob: Blob; url: string }>();
  useEffect(() => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    setObjectUrl({ blob, url });
    return () => URL.revokeObjectURL(url);
  }, [blob]);
  if (!blob || objectUrl?.blob !== blob) return undefined;
  return objectUrl.url;
};

export const useScreenshotImage = (previewId: string, alt: string) => {
  const queryClient = useQueryClient();
  const thumb = useScreenshotThumbQuery(queryClient, previewId);
  const src = useObjectUrl(
    thumb.data?.status === 'available' ? thumb.data.blob : undefined
  );
  const [opening, setOpening] = useState(false);
  const pendingOpen = useRef<AbortController | null>(null);

  // Coming back to the window asks again, so a preview deleted elsewhere
  // stops showing
  useEffect(() => {
    const refetch = () => void refetchScreenshotThumb(queryClient, previewId);
    window.addEventListener('focus', refetch);
    return () => window.removeEventListener('focus', refetch);
  }, [queryClient, previewId]);

  // An open still in flight when the part goes, or shows another preview,
  // never opens the viewer
  useEffect(() => () => pendingOpen.current?.abort(), [previewId]);

  const state: ScreenshotImageState =
    thumb.data?.status === 'unavailable'
      ? { status: 'unavailable' }
      : src
        ? { status: 'shown', src }
        : thumb.isError
          ? { status: 'failed' }
          : { status: 'loading' };

  // The full preview is fetched on every open and never cached, so a preview
  // the backend no longer serves never opens
  const openFullView = async () => {
    pendingOpen.current?.abort();
    const open = new AbortController();
    pendingOpen.current = open;
    setOpening(true);
    try {
      const preview = await fetchScreenshotPreview(
        queryClient,
        previewId,
        'preview',
        open.signal
      );
      if (open.signal.aborted) return;
      if (preview.status === 'unavailable') {
        markScreenshotThumbUnavailable(queryClient, previewId);
        return;
      }
      const url = URL.createObjectURL(preview.blob);
      ModalsManager.show(
        ImagePreviewModal,
        { url, filename: alt, masked: true },
        { onClosed: () => URL.revokeObjectURL(url) }
      );
    } catch (error) {
      if (open.signal.aborted) return;
      logError(error, 'Failed to open a screenshot preview');
      toast.error("Couldn't open the screenshot");
    } finally {
      if (pendingOpen.current === open) {
        pendingOpen.current = null;
        setOpening(false);
      }
    }
  };

  return { state, opening, openFullView };
};
